import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMedicalFormCustomerName } from "./medicalCustomerSheetSave";

const COLUMN_COUNT = 7;
const HEADERS = [
  "No.",
  "Customer",
  "CPR",
  "MOLST",
  "Advanced Directive",
  "Power of Attorney",
  "Hospice",
];

const BASE_COL_WIDTHS_PX = [56, 320, 110, 140, 160, 160, 110];
const MIN_ROW_HEIGHT_PX = 36;
const MIN_WRAPPED_ROW_HEIGHT_PX = 48;
const TITLE_ROW_HEIGHT_PX = 44;
const HEADER_ROW_HEIGHT_PX = 34;
const CHAR_WIDTH_PX = 7.2;
const LINE_HEIGHT_PX = 18;
const PDF_UNICODE_FONT_NAME = "NotoSansSC";
const PDF_UNICODE_FONT_FILE = "NotoSansSC-Regular.ttf";
let pdfUnicodeFontBase64Promise;

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
}

async function loadPdfUnicodeFont() {
  if (!pdfUnicodeFontBase64Promise) {
    pdfUnicodeFontBase64Promise = fetch(`/upload/${PDF_UNICODE_FONT_FILE}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to load PDF font (${response.status}).`);
        }
        const contentType = `${response.headers.get("content-type") ?? ""}`.toLowerCase();
        if (contentType.includes("text/html")) {
          throw new Error("PDF Unicode font is missing from the application build.");
        }
        return arrayBufferToBase64(await response.arrayBuffer());
      })
      .catch((error) => {
        pdfUnicodeFontBase64Promise = undefined;
        throw error;
      });
  }
  return pdfUnicodeFontBase64Promise;
}

async function registerPdfUnicodeFont(doc) {
  const fontBase64 = await loadPdfUnicodeFont();
  doc.addFileToVFS(PDF_UNICODE_FONT_FILE, fontBase64);
  doc.addFont(PDF_UNICODE_FONT_FILE, PDF_UNICODE_FONT_NAME, "normal", "Identity-H");
  doc.addFont(PDF_UNICODE_FONT_FILE, PDF_UNICODE_FONT_NAME, "bold", "Identity-H");
}

/** CJK glyphs render roughly twice as wide as Latin characters. */
function textWidthUnits(text) {
  let units = 0;
  for (const char of `${text ?? ""}`) {
    units += /[\u1100-\u115F\u2E80-\uA4CF\uAC00-\uD7A3\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFF60\uFFE0-\uFFE6]/.test(char)
      ? 2
      : 1;
  }
  return units;
}

function sliceByWidthUnits(text, maxUnits) {
  const chunks = [];
  let current = "";
  let units = 0;
  for (const char of `${text ?? ""}`) {
    const charUnits = textWidthUnits(char);
    if (units + charUnits > maxUnits && current) {
      chunks.push(current);
      current = "";
      units = 0;
    }
    current += char;
    units += charUnits;
  }
  if (current) chunks.push(current);
  return chunks.length > 0 ? chunks : [""];
}

/**
 * Word-wrap plain text so it fits a fixed column width (no column widening).
 * Returns display text with `\n` breaks and the line count.
 */
function wrapTextForColumn(text, widthPx) {
  const raw = `${text ?? ""}`.replace(/\r\n/g, "\n").trim();
  if (!raw) return { text: "", lines: 1 };

  const unitsPerLine = Math.max(6, Math.floor((widthPx - 20) / CHAR_WIDTH_PX));
  const wrappedLines = [];

  raw.split("\n").forEach((paragraph) => {
    const words = `${paragraph}`.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      wrappedLines.push("");
      return;
    }

    let current = "";
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (textWidthUnits(candidate) <= unitsPerLine) {
        current = candidate;
        return;
      }

      if (current) wrappedLines.push(current);

      if (textWidthUnits(word) <= unitsPerLine) {
        current = word;
        return;
      }

      // Hard-break oversized tokens so nothing is clipped horizontally.
      const chunks = sliceByWidthUnits(word, unitsPerLine);
      wrappedLines.push(...chunks.slice(0, -1));
      current = chunks[chunks.length - 1];
    });

    if (current) wrappedLines.push(current);
  });

  const lines = Math.max(1, wrappedLines.length);
  return { text: wrappedLines.join("\n"), lines };
}

/**
 * Build and download a MOLST xlsx that mirrors the in-house MOLST template.
 *
 * The template is hardcoded here (no template file lives in the repo) so the
 * sheet structure / styling stays consistent without an external dependency.
 *
 *  - Row 1       : merged title (bold serif, large, gray background)
 *  - Row 2       : column headers (gray background)
 *  - Rows 3..N+2 : numbered active-member rows
 *
 * Rows are ordered CPR=No first, then CPR=Yes, each group A–Z by last name.
 *
 * @param {Array<{
 *   name: string,
 *   lastName?: string,
 *   firstName?: string,
 *   cpr: string,
 *   molst: string,
 *   advanceDirective: string,
 *   powerOfAttorney: string,
 *   hospice: string,
 * }>} entries
 * @param {string} [filename='MOLST_List']
 * @param {string} [centerName='Worldshine'] Center display name for the title
 */
export async function downloadMolstXlsx(entries, filename = "MOLST_List", centerName = "Worldshine") {
  const model = buildMolstSheetModel(entries, centerName);
  const workbook = await buildMolstWorkbook(model);
  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}

/**
 * Download MOLST as a landscape PDF table matching the sheet columns.
 */
export async function downloadMolstPdf(entries, filename = "MOLST_List", centerName = "Worldshine") {
  const model = buildMolstSheetModel(entries, centerName);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  await registerPdfUnicodeFont(doc);
  const marginX = 28;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFont(PDF_UNICODE_FONT_NAME, "bold");
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  const titleLines = doc.splitTextToSize(model.title, pageWidth - marginX * 2);
  doc.text(titleLines, pageWidth / 2, 36, { align: "center" });

  autoTable(doc, {
    startY: 36 + titleLines.length * 16 + 8,
    head: [model.headers],
    body: model.rows.map((row) => row.map((cell) => `${cell ?? ""}`)),
    styles: {
      font: PDF_UNICODE_FONT_NAME,
      fontStyle: "normal",
      fontSize: 9,
      cellPadding: 4,
      valign: "middle",
      overflow: "linebreak",
      lineColor: [183, 183, 183],
      lineWidth: 0.4,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 36,halign: "center" },
      1: { cellWidth: "auto" },
    },
    margin: { left: marginX, right: marginX },
    // Move a whole row to the next page instead of splitting it across pages.
    rowPageBreak: "avoid",
  });

  doc.save(ensureExtension(filename, ".pdf"));
}

/**
 * Build FortuneSheet workbook data for on-page MOLST viewing.
 * Column widths / row heights are inflated so wrapped text remains fully visible.
 */
export function buildMolstFortuneSheetData(entries, centerName = "Worldshine") {
  const model = buildMolstSheetModel(entries, centerName);
  const celldata = [];
  const merge = {};
  const rowlen = {};
  const customHeight = {};
  const columnlen = {};
  const dataVerification = {};

  model.columnWidthsPx.forEach((width, index) => {
    columnlen[`${index}`] = width;
  });

  const titleText = model.title;
  celldata.push({
    r: 0,
    c: 0,
    v: {
      v: titleText,
      m: titleText,
      ct: { fa: "@", t: "s" },
      ff: "Times New Roman",
      fs: 16,
      bl: 1,
      ht: 0,
      vt: 0,
      tb: "2",
      bg: "#FFFFFF",
      mc: { r: 0, c: 0, rs: 1, cs: COLUMN_COUNT },
    },
  });
  // Keep merged title cells untinted (Fortune may paint non-master cells).
  for (let c = 1; c < COLUMN_COUNT; c += 1) {
    celldata.push({
      r: 0,
      c,
      v: {
        ct: { fa: "@", t: "s" },
        bg: "#FFFFFF",
        mc: { r: 0, c: 0, rs: 1, cs: COLUMN_COUNT },
      },
    });
  }
  merge["0_0"] = { r: 0, c: 0, rs: 1, cs: COLUMN_COUNT };
  rowlen["0"] = model.rowHeightsPx[0];
  customHeight["0"] = 1;

  model.headers.forEach((header, c) => {
    const text = `${header ?? ""}`;
    celldata.push({
      r: 1,
      c,
      v: {
        v: text,
        m: text,
        ct: { fa: "@", t: "s" },
        ff: "Arial",
        fs: 12,
        bl: 1,
        ht: 0,
        vt: 0,
        tb: "2",
        // Match customer-info table header (#0066B1 / white).
        bg: "#0066B1",
        fc: "#FFFFFF",
      },
    });
  });
  rowlen["1"] = model.rowHeightsPx[1];
  customHeight["1"] = 1;

  model.rows.forEach((row, rowIndex) => {
    const r = rowIndex + 2;
    row.forEach((value, c) => {
      const text = `${value ?? ""}`;
      celldata.push({
        r,
        c,
        v: {
          v: text,
          m: text,
          ct: { fa: "@", t: "s" },
          ff: "Arial",
          fs: 12,
          ht: c === 0 ? 0 : 1,
          vt: 0,
          tb: "2",
        },
      });
    });
    rowlen[`${r}`] = model.rowHeightsPx[r];
    customHeight[`${r}`] = 1;
    // CPR is a Yes/No select in Customer Info. Dropdown-only: reject free text.
    dataVerification[`${r}_2`] = {
      type: "dropdown",
      type2: false,
      value1: "Yes,No",
      value2: "",
      checked: false,
      remote: false,
      prohibitInput: true,
      hintShow: false,
      hintText: "",
    };
  });

  return [
    {
      name: "MOLST",
      status: 1,
      order: 0,
      row: Math.max(40, model.rowHeightsPx.length + 8),
      column: COLUMN_COUNT,
      defaultRowHeight: MIN_ROW_HEIGHT_PX,
      celldata,
      dataVerification,
      config: {
        merge,
        rowlen,
        customHeight,
        columnlen,
        // Medical Form renders this title above the frame.
        rowhidden: { "0": 0 },
        authority: {},
      },
      // Freeze through header row (row 1). Hidden title row 0 stays above it.
      frozen: {
        type: "row",
        range: { row_focus: 1, column_focus: 0 },
      },
      scrollLeft: 0,
      scrollTop: 0,
      // Avoid default A1/title-row selection highlight on mount.
      luckysheet_select_save: [],
    },
  ];
}

/**
 * Shared sheet model used by on-page viewer, XLSX, and PDF.
 */
export function buildMolstSheetModel(entries, centerName = "Worldshine") {
  const rowsData = sortMolstEntries(Array.isArray(entries) ? entries : []);
  const titleCenter = `${centerName ?? ""}`.trim() || "Worldshine";
  const title = `${titleCenter} Life Support Legal Documentation (Exported on ${formatExportDate(new Date())})`;

  // Columns stay at a fixed width; every renderer wraps long text in place.
  const columnWidthsPx = [...BASE_COL_WIDTHS_PX];
  const totalWidth = columnWidthsPx.reduce((sum, w) => sum + w, 0);

  const rows = rowsData.map((entry, index) => [
    `${index + 1}`,
    `${entry?.name ?? ""}`,
    `${entry?.cpr ?? ""}`,
    `${entry?.molst ?? ""}`,
    `${entry?.advanceDirective ?? ""}`,
    `${entry?.powerOfAttorney ?? ""}`,
    `${entry?.hospice ?? ""}`,
  ]);

  const headers = [...HEADERS];
  const headerLines = Math.max(
    1,
    ...headers.map((header, colIndex) => wrapTextForColumn(header, columnWidthsPx[colIndex]).lines)
  );
  const titleLines = wrapTextForColumn(title, totalWidth).lines;

  const rowHeightsPx = [
    Math.max(TITLE_ROW_HEIGHT_PX, titleLines * 22 + 12),
    Math.max(HEADER_ROW_HEIGHT_PX, headerLines * LINE_HEIGHT_PX + 10),
  ];

  rows.forEach((row) => {
    const maxLines = row.reduce(
      (lines, value, colIndex) => Math.max(lines, wrapTextForColumn(value, columnWidthsPx[colIndex]).lines),
      1
    );
    const minHeight = maxLines > 1 ? MIN_WRAPPED_ROW_HEIGHT_PX : MIN_ROW_HEIGHT_PX;
    rowHeightsPx.push(Math.max(minHeight, maxLines * LINE_HEIGHT_PX + 12));
  });

  return {
    title,
    headers,
    rows,
    columnWidthsPx,
    rowHeightsPx,
  };
}

async function buildMolstWorkbook(model) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("MOLST");

  const THICK_BLACK = { style: "thin", color: { argb: "FF000000" } };
  const LIGHT_GRAY = { style: "thin", color: { argb: "FFB7B7B7" } };
  const lastColLetter = String.fromCharCode(64 + COLUMN_COUNT);

  sheet.columns = model.columnWidthsPx.map((px) => ({
    width: Math.max(6, Math.round(px / 7)),
  }));

  const titleRow = sheet.addRow([model.title, ...Array(COLUMN_COUNT - 1).fill(null)]);
  titleRow.height = Math.max(30, Math.round((model.rowHeightsPx[0] || TITLE_ROW_HEIGHT_PX) * 0.75));
  sheet.mergeCells(`A1:${lastColLetter}1`);
  const titleCell = sheet.getCell("A1");
  titleCell.font = { name: "Times New Roman", size: 16, bold: true, color: { argb: "FF000000" } };
  titleCell.alignment = { horizontal: "center", vertical: "center", wrapText: true };
  titleCell.border = {
    top: THICK_BLACK,
    left: THICK_BLACK,
    right: THICK_BLACK,
    bottom: THICK_BLACK,
  };

  const headerRow = sheet.addRow(model.headers);
  headerRow.height = Math.max(24, Math.round((model.rowHeightsPx[1] || HEADER_ROW_HEIGHT_PX) * 0.75));
  headerRow.eachCell((cell) => {
    cell.font = { name: "Arial", size: 12, color: { argb: "FF000000" } };
    cell.alignment = { horizontal: "center", vertical: "center", wrapText: true };
    cell.border = {
      top: THICK_BLACK,
      left: THICK_BLACK,
      right: THICK_BLACK,
      bottom: THICK_BLACK,
    };
  });

  model.rows.forEach((rowValues, index) => {
    const dataRow = sheet.addRow(rowValues);
    dataRow.height = Math.max(24, Math.round((model.rowHeightsPx[index + 2] || MIN_ROW_HEIGHT_PX) * 0.75));
    dataRow.eachCell((cell, colNumber) => {
      cell.font = { name: "Arial", size: 12, color: { argb: "FF000000" } };
      cell.alignment = {
        horizontal: colNumber === 1 ? "center" : "left",
        vertical: "center",
        wrapText: true,
      };
      cell.border = {
        top: LIGHT_GRAY,
        left: colNumber === 1 ? THICK_BLACK : LIGHT_GRAY,
        right: colNumber === COLUMN_COUNT ? THICK_BLACK : LIGHT_GRAY,
        bottom: LIGHT_GRAY,
      };
    });
  });

  if (model.rows.length > 0) {
    const lastDataRowIndex = sheet.rowCount;
    for (let col = 1; col <= COLUMN_COUNT; col += 1) {
      const cell = sheet.getRow(lastDataRowIndex).getCell(col);
      cell.border = {
        ...cell.border,
        bottom: THICK_BLACK,
      };
    }
  }

  return workbook;
}

function triggerBlobDownload(buffer, mimeType, outputName) {
  const blob = new Blob([buffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = outputName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function ensureExtension(filename, extension) {
  const safeBase = `${String(filename || "MOLST_List")}`.replace(/[/\\?%*:|"<>]/g, "-");
  const lower = safeBase.toLowerCase();
  if (lower.endsWith(extension.toLowerCase())) return safeBase;
  return `${safeBase}${extension}`;
}

/**
 * True when at least one legal/MOLST field is populated on the customer record.
 */
export function hasMolstLegalData(customer) {
  if (customer == null) return false;
  return [
    customer.burial_arrangements,
    customer.molst,
    customer.provisions_for_advance_medical,
    customer.power_of_attorney,
    customer.hospice,
  ].some((value) => `${value ?? ""}`.trim() !== "");
}

/**
 * Build one export row from customer legal/MOLST fields.
 */
export function buildMolstEntry(customer) {
  return {
    id: `${customer?._id ?? customer?.id ?? ""}`,
    name: buildMolstCustomerName(customer),
    lastName: `${customer?.lastname ?? customer?.lastName ?? ""}`.trim(),
    firstName: `${customer?.firstname ?? customer?.firstName ?? ""}`.trim(),
    middleName: `${customer?.middle_name ?? customer?.middleName ?? ""}`.trim(),
    cpr: formatMolstFieldValue(customer?.burial_arrangements),
    molst: formatMolstFieldValue(customer?.molst),
    advanceDirective: formatMolstFieldValue(customer?.provisions_for_advance_medical),
    powerOfAttorney: formatMolstFieldValue(customer?.power_of_attorney),
    hospice: formatMolstFieldValue(customer?.hospice),
  };
}

/**
 * Customer column: Last Name, First Name Middle Name (Preferred Name).
 */
export function buildMolstCustomerName(customer) {
  return formatMedicalFormCustomerName(customer);
}

function formatMolstFieldValue(value) {
  if (value === "yes" || value === true) return "Yes";
  if (value === "no" || value === false) return "No";
  return `${value ?? ""}`.trim();
}

/**
 * CPR=No first, CPR=Yes second, then other/blank; within each group A–Z by last name.
 */
export function sortMolstEntries(entries) {
  return [...(Array.isArray(entries) ? entries : [])].sort((a, b) => {
    const byCpr = cprSortRank(a?.cpr) - cprSortRank(b?.cpr);
    if (byCpr !== 0) return byCpr;
    const lastA = `${a?.lastName ?? ""}`.trim();
    const lastB = `${b?.lastName ?? ""}`.trim();
    const byLast = lastA.localeCompare(lastB, undefined, { sensitivity: "base", numeric: true });
    if (byLast !== 0) return byLast;
    const firstA = `${a?.firstName ?? ""}`.trim();
    const firstB = `${b?.firstName ?? ""}`.trim();
    const byFirst = firstA.localeCompare(firstB, undefined, { sensitivity: "base", numeric: true });
    if (byFirst !== 0) return byFirst;
    const middleA = `${a?.middleName ?? ""}`.trim();
    const middleB = `${b?.middleName ?? ""}`.trim();
    return middleA.localeCompare(middleB, undefined, { sensitivity: "base", numeric: true });
  });
}

function cprSortRank(cpr) {
  const v = `${cpr ?? ""}`.trim().toLowerCase();
  if (v === "no" || v === "false") return 0;
  if (v === "yes" || v === "true") return 1;
  return 2;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatExportDate(date) {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const month = MONTH_NAMES[d.getMonth()];
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month} ${day}, ${year}`;
}
